<script>
import axios from 'axios'
import TwButton from '@/components/generic/tw-button.vue'
import TwCheckbox from '@/components/generic/forms/tw-checkbox.vue'
import TwInput from '@/components/generic/forms/tw-input.vue'
import TwModal from '@/components/generic/modal/tw-modal.vue'
import TwPassword from '@/components/generic/forms/tw-password.vue'
import TwRadio from '@/components/generic/forms/tw-radio.vue'
import TwSelect from '@/components/generic/forms/tw-select.vue'
import TwTextarea from '@/components/generic/forms/tw-textarea.vue'
import TwValidationErrors from '@/components/generic/tw-validation-errors.vue'
import { useAuthStore } from '@/stores/auth.js'

export default {
  name: 'OrganizationRegisterChatModal',
  components: { TwButton, TwCheckbox, TwInput, TwModal, TwPassword, TwRadio, TwSelect, TwTextarea, TwValidationErrors },
  props: {
    resetOnOpen: {
      type: Boolean,
      default: true
    }
  },
  emits: ['opened', 'closed', 'registered', 'validation-error', 'signup-error', 'workflow-error'],
  data() {
    return {
      Workflow: null,
      Answers: {},
      Messages: [],
      CurrentFieldIndex: 0,
      CurrentAnswer: null,
      LoadingWorkflow: false,
      LoadingValidation: false,
      LoadingSigningUp: false,
      ValidationErrorMessage: null,
      SignupErrorMessage: null,
      WorkflowErrorMessage: null,
      ReviewMode: false
    }
  },
  computed: {
    fields() {
      return this.Workflow?.fields || []
    },
    visibleFields() {
      return this.fields.filter((field) => this.shouldShowField(field))
    },
    currentField() {
      return this.visibleFields[this.CurrentFieldIndex] || null
    },
    payload() {
      return {
        ...this.Workflow?.defaults,
        ...this.Answers
      }
    },
    usingExistingAccount() {
      return this.payload.user_existing_account === true || this.payload.user_existing_account === 1 || this.payload.user_existing_account === '1'
    },
    progressLabel() {
      if (!this.visibleFields.length) return null
      return `${Math.min(this.CurrentFieldIndex + 1, this.visibleFields.length)} of ${this.visibleFields.length}`
    },
    reviewFields() {
      return this.visibleFields.filter((field) => !field.sensitive)
    },
    canGoBack() {
      return !this.ReviewMode && this.CurrentFieldIndex > 0 && !this.LoadingValidation
    }
  },
  watch: {
    Messages: {
      handler() {
        this.scrollChatToBottom()
      },
      deep: true
    }
  },
  methods: {
    async open() {
      this.$refs.modal.open()
      await this.loadWorkflow()

      if (this.Workflow && this.resetOnOpen) {
        this.resetConversation()
      }
    },
    async close() {
      await this.$refs.modal.close()
    },
    opened() {
      this.$emit('opened')
    },
    closed() {
      this.$emit('closed')
    },
    async loadWorkflow() {
      if (this.Workflow || this.LoadingWorkflow) return

      try {
        this.WorkflowErrorMessage = null
        this.LoadingWorkflow = true
        const { data } = await axios.get('/api/v1/signup/workflow')
        this.Workflow = data
        this.resetConversation()
      } catch (e) {
        if (e.response) {
          this.WorkflowErrorMessage = e.response.data
        } else {
          this.WorkflowErrorMessage = {
            message: 'The registration workflow could not be loaded.'
          }
        }
        this.$emit('workflow-error', this.WorkflowErrorMessage)
      } finally {
        this.LoadingWorkflow = false
      }
    },
    inputComponent(field) {
      if (!field) return TwInput
      if (field.type === 'textarea') return TwTextarea
      if (field.type === 'radio') return TwRadio
      if (field.type === 'select') return TwSelect
      if (field.type === 'password') return TwPassword
      return TwInput
    },
    inputProps(field) {
      if (!field) return {}

      const props = {
        label: this.labelFor(field),
        required: field.required,
        placeholder: this.promptFor(field)
      }

      if (field.type === 'select') {
        props.options = this.optionsForField(field)
        props.labelKey = 'label'
        props.valueKey = 'value'
      }

      if (field.type === 'radio') {
        props.options = this.optionsForField(field).map((option) => ({
          value: option.value,
          text: option.text || option.label
        }))
      }

      return props
    },
    optionsForField(field) {
      if (field?.key === 'organization_address_state') {
        return field.options_by_country?.[this.payload.organization_address_country] || []
      }

      return field?.options || []
    },
    shouldShowField(field) {
      if (!field) return false

      if (field.key === 'organization_nonprofit') {
        return false
      }

      if (['user_name', 'user_email_confirm', 'user_password_confirmation'].includes(field.key)) {
        return !this.usingExistingAccount
      }

      return true
    },
    labelFor(field) {
      if (field?.key === 'user_password' && this.usingExistingAccount) return 'Current password'
      if (field?.key === 'user_password') return 'Create password'

      return field?.label
    },
    promptFor(field) {
      if (field?.key === 'user_email' && this.usingExistingAccount) {
        return 'What email address is on your existing account?'
      }

      if (field?.key === 'user_email') {
        return 'What email address should you use to sign in?'
      }

      if (field?.key === 'user_password' && this.usingExistingAccount) {
        return 'Enter the current password for this account.'
      }

      if (field?.key === 'user_password') {
        return 'Create a password with at least 8 characters.'
      }

      return field?.prompt
    },
    displayAnswer(field, value) {
      if (field?.sensitive) return 'Provided'
      if (field?.type === 'boolean') return value ? 'Yes' : 'No'
      if (field?.type === 'select' || field?.type === 'radio') {
        const option = this.optionsForField(field).find((option) => option.value === value)

        return option?.label || option?.text || value
      }
      return value
    },
    validationFieldsFor(field) {
      return [...(field.depends_on || []), field.key]
    },
    fieldIndexFor(fieldKey) {
      return this.visibleFields.findIndex((field) => field.key === fieldKey)
    },
    validationPayloadFor(answers) {
      return this.normalizeAnswersForAccountMode({
        ...this.Workflow.defaults,
        ...answers,
        user_existing_account: answers.user_existing_account === true || answers.user_existing_account === 1 || answers.user_existing_account === '1'
      })
    },
    normalizeAnswersForAccountMode(answers) {
      const usingExistingAccount = answers.user_existing_account === true || answers.user_existing_account === 1 || answers.user_existing_account === '1'

      if (usingExistingAccount) {
        return {
          ...answers,
          user_name: null,
          user_email_confirm: null,
          user_password_confirmation: null
        }
      }

      return answers
    },
    signupPayload() {
      return this.normalizeAnswersForAccountMode({
        ...this.payload,
        user_existing_account: this.usingExistingAccount
      })
    },
    scrollChatToBottom() {
      this.$nextTick(() => {
        const chat = this.$refs.registrationChat
        if (!chat) return

        chat.scrollTop = chat.scrollHeight
      })
    },
    jumpToField(fieldKey) {
      if (this.LoadingValidation || this.LoadingSigningUp) return

      const index = this.fieldIndexFor(fieldKey)
      if (index === -1) return

      const field = this.visibleFields[index]

      this.CurrentFieldIndex = index
      this.CurrentAnswer = this.Answers[field.key] ?? null
      this.ReviewMode = false
      this.ValidationErrorMessage = null
      this.SignupErrorMessage = null
      this.Messages.push({
        role: 'assistant',
        text: this.promptFor(field),
        fieldKey: field.key
      })
    },
    previousField() {
      if (!this.canGoBack) return

      this.jumpToField(this.visibleFields[this.CurrentFieldIndex - 1].key)
    },
    isEditableAnswer(message) {
      return message.role === 'user' && message.fieldKey && this.fieldIndexFor(message.fieldKey) !== -1
    },
    submitCurrentAnswerFromEnter(event) {
      if (event.isComposing || event.keyCode === 229 || this.LoadingValidation) return

      event.preventDefault()
      this.submitCurrentAnswer()
    },
    async submitCurrentAnswer() {
      if (!this.currentField) return

      this.ValidationErrorMessage = null
      this.SignupErrorMessage = null
      const field = this.currentField
      const value = field.type === 'boolean' ? Boolean(this.CurrentAnswer) : this.CurrentAnswer

      if (field.required && (value === null || value === undefined || value === '')) {
        this.ValidationErrorMessage = {
          message: 'The selected field is required.',
          errors: {
            [field.key]: [`The ${this.labelFor(field).toLowerCase()} field is required.`]
          }
        }
        this.$emit('validation-error', this.ValidationErrorMessage)
        return
      }

      let nextAnswers = {
        ...this.Answers,
        [field.key]: value
      }

      if (field.key === 'organization_address_country') {
        nextAnswers = {
          ...nextAnswers,
          organization_address_state: null,
          organization_address_zip: null
        }
      }

      if (field.key === 'organization_address_state') {
        nextAnswers = {
          ...nextAnswers,
          organization_address_zip: null
        }
      }

      if (field.key === 'user_existing_account') {
        nextAnswers = this.normalizeAnswersForAccountMode({
          ...nextAnswers,
          user_password: null,
          user_password_confirmation: null
        })
      }

      try {
        this.LoadingValidation = true
        await axios.post('/api/v1/signup', this.validationPayloadFor(nextAnswers), {
          headers: {
            'Precognition': 'true',
            'Precognition-Validate-Only': this.validationFieldsFor(field).join(',')
          }
        })

        this.Answers = nextAnswers
        this.Messages.push({
          role: 'user',
          text: this.displayAnswer(field, value),
          fieldKey: field.key
        })
        this.advanceConversation()
      } catch (e) {
        if (e.response) this.ValidationErrorMessage = e.response.data
        this.$emit('validation-error', this.ValidationErrorMessage)
      } finally {
        this.LoadingValidation = false
      }
    },
    advanceConversation() {
      const nextIndex = this.CurrentFieldIndex + 1

      if (nextIndex >= this.visibleFields.length) {
        this.ReviewMode = true
        this.Messages.push({
          role: 'assistant',
          text: 'I have everything needed. Review the details below before creating the organization account.'
        })
        return
      }

      this.CurrentFieldIndex = nextIndex
      this.CurrentAnswer = this.Answers[this.currentField.key] ?? null
      this.Messages.push({
        role: 'assistant',
        text: this.promptFor(this.currentField),
        fieldKey: this.currentField.key
      })
    },
    editField(field) {
      this.jumpToField(field.key)
    },
    resetConversation() {
      if (!this.Workflow) return

      this.Answers = { ...this.Workflow.defaults }
      this.CurrentFieldIndex = 0
      this.CurrentAnswer = this.Answers[this.currentField?.key] ?? null
      this.ReviewMode = false
      this.ValidationErrorMessage = null
      this.SignupErrorMessage = null
      this.Messages = [
        {
          role: 'assistant',
          text: 'I will collect the information needed to register your organization. You can review everything before the account is created.'
        },
        {
          role: 'assistant',
          text: this.promptFor(this.currentField),
          fieldKey: this.currentField?.key
        }
      ]
    },
    async signup() {
      try {
        this.SignupErrorMessage = null
        this.LoadingSigningUp = true
        const { data } = await axios.post(this.Workflow.submit_endpoint, this.signupPayload())

        if (data.autologin_token) {
          this.$emit('registered', data)
          await useAuthStore().loginViaToken(data.autologin_token)
          await this.$refs.modal.close()
          await this.$router.push({ name: 'DashboardPage' })
        }
      } catch (e) {
        if (e.response) this.SignupErrorMessage = e.response.data
        this.$emit('signup-error', this.SignupErrorMessage)
      } finally {
        this.LoadingSigningUp = false
      }
    }
  }
}
</script>

<template>
  <tw-modal ref="modal" no-footer size="lg" :loading="LoadingSigningUp" modal-id="organization-register-chat-modal" @opened="opened" @closed="closed">
    <div>
      <div class="mb-6">
        <p class="text-xs uppercase tracking-[0.22em] text-gray-500">
          Organization Signup
        </p>
        <h3 class="mt-1">
          Register through a guided conversation
        </h3>
        <p v-if="progressLabel && !ReviewMode" class="text-sm text-gray-500">
          Step {{ progressLabel }}
        </p>
      </div>

      <div v-if="LoadingWorkflow" class="rounded-xl bg-gray-50 p-6 text-sm text-gray-600">
        Loading registration workflow...
      </div>

      <div v-else class="space-y-5">
        <div v-if="!ReviewMode" ref="registrationChat" class="max-h-[340px] space-y-3 overflow-y-auto rounded-2xl bg-slate-50 p-4">
          <div v-for="(message, index) in Messages" :key="'registration-chat-message-' + index" :class="['flex', { 'justify-end': message.role === 'user' }]">
            <button
              v-if="isEditableAnswer(message)"
              type="button"
              class="max-w-[85%] rounded-2xl bg-gray-900 px-4 py-3 text-left text-sm text-white ring-offset-2 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500"
              @click="jumpToField(message.fieldKey)"
            >
              {{ message.text }}
            </button>
            <div v-else :class="['max-w-[85%] rounded-2xl px-4 py-3 text-sm', message.role === 'user' ? 'bg-gray-900 text-white' : 'bg-white text-gray-800 shadow-sm']">
              {{ message.text }}
            </div>
          </div>
        </div>

        <tw-validation-errors :validation-result-error="ValidationErrorMessage" @close="ValidationErrorMessage = null" />
        <tw-validation-errors :validation-result-error="SignupErrorMessage" @close="SignupErrorMessage = null" />
        <tw-validation-errors :validation-result-error="WorkflowErrorMessage" @close="WorkflowErrorMessage = null" />

        <div v-if="!ReviewMode && currentField" class="rounded-2xl border border-gray-200 p-4">
          <tw-checkbox v-if="currentField.type === 'boolean'" v-model="CurrentAnswer" :label="currentField.label" @keydown.enter="submitCurrentAnswerFromEnter" />
          <component :is="inputComponent(currentField)" v-else v-model="CurrentAnswer" v-bind="inputProps(currentField)" @keydown.enter="submitCurrentAnswerFromEnter" />

          <div class="mt-5 flex justify-between gap-2">
            <tw-button variant="secondary" :disabled="!canGoBack" @click="previousField">
              Back
            </tw-button>
            <tw-button :loading="LoadingValidation" @click="submitCurrentAnswer">
              Continue
            </tw-button>
          </div>
        </div>

        <div v-if="ReviewMode" class="rounded-2xl border border-gray-200 p-4">
          <h4 class="mb-3">
            Review registration details
          </h4>
          <dl class="divide-y divide-gray-100">
            <div v-for="field in reviewFields" :key="'registration-review-' + field.key" class="grid gap-2 py-3 sm:grid-cols-3">
              <dt class="text-sm font-semibold text-gray-700">
                {{ field.label }}
              </dt>
              <dd class="text-sm text-gray-900 sm:col-span-2">
                <span>{{ displayAnswer(field, payload[field.key]) }}</span>
                <button type="button" class="ml-3 text-xs font-semibold text-gray-600 underline" @click="editField(field)">
                  Edit
                </button>
              </dd>
            </div>
          </dl>
          <div class="mt-5 flex justify-end gap-2">
            <tw-button variant="secondary" @click="resetConversation">
              Start over
            </tw-button>
            <tw-button :loading="LoadingSigningUp" @click="signup">
              Create organization account
            </tw-button>
          </div>
        </div>
      </div>
    </div>
  </tw-modal>
</template>
